import { useMemo } from 'react'
import {
  BreadcrumbObjectType,
  useGetFileCellDocumentQuery,
  useGetFormQuery,
  useGetFormV2SharedQuery,
  useGetSimpleTableQuery,
} from '~/common/generated/apollo-operations'
import { isSharedPath as checkIsSharedPath } from '~/data/paths'
import { usePage } from './usePage'

/**
 * Hook that exposes breadcrumb information + simple data about a sharable object
 */
export function useSharedObject() {
  const { objectId, objectType, pathname } = usePage()

  const isSharedPath = checkIsSharedPath(pathname)

  const { data: simpleTableData } = useGetSimpleTableQuery({
    variables: {
      tableId: objectId || '',
    },
    skip: !isSharedPath || !objectId || objectType !== BreadcrumbObjectType.TABLE,
  })

  const { data: simpleFormV1Data } = useGetFormQuery({
    variables: {
      formId: objectId || '',
    },
    skip: !isSharedPath || !objectId || objectType !== BreadcrumbObjectType.FORM,
  })

  const { data: simpleFormV2Data } = useGetFormV2SharedQuery({
    variables: {
      formId: objectId || '',
    },
    skip: !isSharedPath || !objectId || objectType !== BreadcrumbObjectType.FORM,
  })

  const { data: simpleDocumentData } = useGetFileCellDocumentQuery({
    variables: {
      documentId: objectId || '',
    },
    skip: !isSharedPath || !objectId || objectType !== BreadcrumbObjectType.DOCUMENT,
  })

  const data = useMemo(() => {
    if (!objectType || !objectId) {
      return undefined
    }

    if (!simpleTableData && !simpleFormV1Data && !simpleFormV2Data && !simpleDocumentData) {
      return undefined
    }

    switch (objectType) {
      case BreadcrumbObjectType.DOCUMENT: {
        if (!simpleDocumentData) {
          return undefined
        }

        return {
          objectId: simpleDocumentData.document.id,
          objectName: simpleDocumentData.document.name,
          workspaceType: BreadcrumbObjectType.TABLE,
        }
      }
      case BreadcrumbObjectType.FORM: {
        if (simpleFormV2Data) {
          return {
            objectId: simpleFormV2Data.formV2.id,
            objectName: simpleFormV2Data.formV2.name,
            workspaceType: BreadcrumbObjectType.FORM_V2,
          }
        }

        if (simpleFormV1Data) {
          return {
            objectId: simpleFormV1Data.form.id,
            objectName: simpleFormV1Data.form.name,
            workspaceType: BreadcrumbObjectType.FORM,
          }
        }

        return undefined
      }
      case BreadcrumbObjectType.TABLE: {
        if (!simpleTableData) {
          return undefined
        }

        return {
          objectId: simpleTableData.table.id,
          objectName: simpleTableData.table.name,
          workspaceType: BreadcrumbObjectType.TABLE,
        }
      }
      case BreadcrumbObjectType.CHART:
      case BreadcrumbObjectType.EMBED:
      case BreadcrumbObjectType.ACTION:
      case BreadcrumbObjectType.PIVOT_TABLE:
      case BreadcrumbObjectType.PROJECT:
      case BreadcrumbObjectType.TABLE_ROW:
      case BreadcrumbObjectType.FORM_CATEGORY:
      case BreadcrumbObjectType.WORKFLOW:
      case BreadcrumbObjectType.WORKFLOW_V2:
      case BreadcrumbObjectType.WORKFLOW_V2_TRIGGER:
      case BreadcrumbObjectType.ACTION_STEP:
      case BreadcrumbObjectType.ORGANIZATION:
      case BreadcrumbObjectType.USER:
      case BreadcrumbObjectType.FORM_V2:
      case BreadcrumbObjectType.FORM_V2_INPUT:
      case BreadcrumbObjectType.FORM_FIELD:
      case BreadcrumbObjectType.FORM_PAGE:
      case BreadcrumbObjectType.TABLE_CELL:
      case BreadcrumbObjectType.TABLE_COLUMN:
      case BreadcrumbObjectType.WORKSPACE:
        return undefined
    }
  }, [
    simpleTableData,
    simpleFormV1Data,
    simpleFormV2Data,
    simpleDocumentData,
    objectId,
    objectType,
  ])

  return data
}
